import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, RefreshCw, Save, Search, X } from 'lucide-react';
import MainService from '../../services/MainService';
import ClientListGrid, { useClientListGrid } from '../ui/ClientListGrid';

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const isSuccess = (response) => response === true || response === 'true' || response === 'Success';

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB');
};

const formatAmount = (value) => Number(value || 0).toFixed(2);

const ORDER_COLUMNS = [
  { field: 'voucherno', title: 'Voucher No', hideable: true },
  { field: 'deliverydate', title: 'Delivery Date', hideable: true },
  { field: 'client', title: 'Client', hideable: true },
  { field: 'clientadd', title: 'Client Add', hideable: true },
  { field: 'customer', title: 'Customer', hideable: true },
  { field: 'customeradd', title: 'Customer Add', hideable: true },
  { field: 'proprice', title: 'Pro Price', hideable: true },
  { field: 'shipcharge', title: 'Ship Charge', hideable: true },
  { field: 'tax', title: 'Tax', hideable: true },
  { field: 'deliverycharge', title: 'Delivery Charge', hideable: true },
  { field: 'paytype', title: 'Pay Type', hideable: true },
  { field: 'atps', title: 'Atps', hideable: true },
  { field: 'status', title: 'Status', hideable: true },
  { field: 'storerefno', title: 'Store Ref No', hideable: true },
  { field: 'storerefnew', title: 'Store Ref New', hideable: true, sortable: false, filterable: false, groupable: false },
];

const getCellValue = (row, field) => {
  switch (field) {
    case 'voucherno':
      return row.VoucherNo || '';
    case 'deliverydate':
      return formatDate(row.DeliveryDate);
    case 'client':
      return [row.ClientName, row.ClientPhone].filter(Boolean).join(' ');
    case 'clientadd':
      return [row.ClEmitare, row.ClLoc].filter(Boolean).join(', ');
    case 'customer':
      return [row.CustomerName, row.CustomerPhone].filter(Boolean).join(' ');
    case 'customeradd':
      return [row.CustEmirate, row.CustLoc].filter(Boolean).join(', ');
    case 'proprice':
      return formatAmount(row.ProductPrice);
    case 'shipcharge':
      return formatAmount(row.ServiceCharge);
    case 'tax':
      return formatAmount(row.TAX);
    case 'deliverycharge':
      return formatAmount(row.DeliveryCharge);
    case 'paytype':
      return String(row.PayType) === '1' ? 'Customer' : 'Client';
    case 'atps':
      return String(row.Attempts ?? 0);
    case 'status':
      return row.VoucherStatus || '';
    case 'storerefno':
      return String(row.StoreRefNo ?? 0);
    case 'storerefnew':
      return String(row.StoreRefNoNew ?? '');
    default:
      return row[field] ?? '';
  }
};

const ChangeStoreRef = () => {
  const [areas, setAreas] = useState([]);
  const [orders, setOrders] = useState([]);
  const [areaId, setAreaId] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const columns = useMemo(() => ORDER_COLUMNS, []);
  const grid = useClientListGrid({ columns, rows: orders, getCellValue });
  const editedCount = useMemo(() => orders.filter((order) => String(order.StoreRefNoNew ?? '').trim() !== '').length, [orders]);

  const loadAreas = useCallback(async () => {
    try {
      const response = await MainService.AreasInDropDown();
      setAreas(parseList(response));
    } catch (err) {
      console.error('Load areas error:', err);
      setAreas([]);
      setError('Failed to load areas');
    }
  }, []);

  useEffect(() => {
    loadAreas();
  }, [loadAreas]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const filterOrders = async () => {
    if (!areaId) {
      setError('Select Area');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await MainService.GetReturnedOrdersByAreaId({ AreaId: Number(areaId) });
      setOrders(parseList(response).map((order) => ({ ...order, StoreRefNoNew: order.StoreRefNoNew || '' })));
    } catch (err) {
      console.error('Filter returned orders error:', err);
      setOrders([]);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  const updateStoreRefDraft = (orderId, value) => {
    setOrders((prev) => prev.map((order) => (order.Id === orderId ? { ...order, StoreRefNoNew: value } : order)));
  };

  const saveOrders = async () => {
    if (orders.length === 0) {
      setError('No orders to save');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const payload = orders.map((order) => ({
        ...order,
        StoreRefNoNew: Number(order.StoreRefNoNew) || 0,
      }));
      const response = await MainService.SaveStoreRefReturnOrders(payload);
      if (isSuccess(response)) {
        setSuccess('Success');
        setAreaId('');
        setOrders([]);
      } else {
        setError('Network Error');
      }
    } catch (err) {
      console.error('Save returned order store refs error:', err);
      setError('Network Error');
    } finally {
      setSaving(false);
    }
  };

  const refresh = () => {
    setAreaId('');
    setOrders([]);
    loadAreas();
  };

  return (
    <div className="space-y-6">
      {error && <Toast type="error" message={error} onClose={() => setError('')} />}
      {success && <Toast type="success" message={success} onClose={() => setSuccess('')} />}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex flex-col gap-4 border-b border-gray-200 pb-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Change Store Ref</h1>
            <p className="mt-1 text-sm text-gray-500">Filter returned orders by area and assign new store reference numbers.</p>
          </div>
          <button
            type="button"
            onClick={refresh}
            disabled={loading || saving}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-4">
            <label className="mb-1 block text-sm font-medium text-gray-700">Area</label>
            <select
              value={areaId}
              onChange={(event) => setAreaId(event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="">Select Area</option>
              {areas.map((area) => (
                <option key={area.Id} value={area.Id}>
                  {area.Name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap gap-2 lg:col-span-8">
            <button
              type="button"
              onClick={filterOrders}
              disabled={loading || saving}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
            >
              <Search className="h-4 w-4" />
              Filter
            </button>
            <button
              type="button"
              onClick={saveOrders}
              disabled={saving || orders.length === 0}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              Save New Numbers
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-2 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Returned Orders</h2>
            <p className="text-sm text-gray-500">
              {orders.length} orders found, {editedCount} new numbers entered
            </p>
          </div>
        </div>

        <ClientListGrid
          columns={columns}
          grid={grid}
          loading={loading}
          loadingText="Loading orders..."
          emptyText="No returned orders found."
          minWidth="1400px"
          compact
          rowKey={(row) => row.Id}
          renderCell={(row, column) => {
            if (column.field === 'client') {
              return (
                <div>
                  <div>{row.ClientName || '-'}</div>
                  {row.ClientPhone ? <div className="text-xs text-gray-500">{row.ClientPhone}</div> : null}
                </div>
              );
            }
            if (column.field === 'customer') {
              return (
                <div>
                  <div>{row.CustomerName || '-'}</div>
                  {row.CustomerPhone ? <div className="text-xs text-gray-500">{row.CustomerPhone}</div> : null}
                </div>
              );
            }
            if (column.field === 'storerefnew') {
              return (
                <input
                  type="number"
                  value={row.StoreRefNoNew ?? ''}
                  onChange={(event) => updateStoreRefDraft(row.Id, event.target.value)}
                  className="w-28 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              );
            }
            if (column.field === 'voucherno') {
              return <span className="font-semibold text-gray-900">{row.VoucherNo || '-'}</span>;
            }
            return getCellValue(row, column.field) || '-';
          }}
        />
      </div>
    </div>
  );
};

const Toast = ({ type, message, onClose }) => {
  const isError = type === 'error';
  return (
    <div className={`fixed right-4 top-4 z-50 flex min-w-[280px] items-center gap-3 rounded-lg px-4 py-3 text-white shadow-lg ${isError ? 'bg-red-500' : 'bg-green-500'}`}>
      {isError ? <AlertTriangle className="h-5 w-5" /> : <CheckCircle className="h-5 w-5" />}
      <span className="flex-1 text-sm font-medium">{message}</span>
      <button type="button" onClick={onClose} className="rounded p-1 hover:bg-white/20">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default ChangeStoreRef;
